import {
  Box,
  Flex,
  Text,
  Avatar,
  Tooltip,
  useColorModeValue,
  Heading,
} from "@chakra-ui/react";
import { FC, useMemo } from "react";
import VerifiedDot from "../../atoms/VerifiedDot";
import ChannelSocialIcons from "../ChannelSocialIcons";
import { Channel } from "@/schemaTypes";
import { formatFollowers } from "../../../utils/formatFollowers";
import { CategoryTag } from "../../atoms/CategoryTag";
import ChannelFollowingButton from "../../atoms/ChannelFollowingButton";
import { sanitizeChannelDescription } from "../../../utils/sanitizeChannelDescription";

interface ChannelInfoSectionProps {
  channel: Channel;
  isFollowing: boolean;
  onToggleFollow?: (channelId: string) => void;
  /** Loading while follow/unfollow mutation is in flight */
  isFollowLoading?: boolean;
  isUserLoggedIn: boolean;
  onLoginRequired?: (channelId: string) => void;
}

export const ChannelInfoSection: FC<ChannelInfoSectionProps> = ({
  channel,
  isFollowing,
  onToggleFollow,
  isFollowLoading,
  isUserLoggedIn,
  onLoginRequired,
}) => {
  const {
    name,
    verified,
    imageUrl,
    followers,
    categories,
    description,
    social,
  } = channel;

  const displayIsFollowing = isFollowing;
  const displayFollowers = followers ?? 0;

  const sanitizedDescription = useMemo(
    () => sanitizeChannelDescription(description ?? ""),
    [description]
  );

  return (
    <Flex flexDir="column" gap={{ base: "24px", md: "32px" }}>
      <Flex
        gap={{ base: "16px", md: "28px" }}
        flexDir={{ base: "column", md: "row" }}
        alignItems={{ base: "center", md: "start" }}
      >
        {/* Avatar column */}
        <Box
          flexShrink={0}
          position="relative"
          zIndex={1}
          alignSelf={{ base: "center", md: "start" }}
        >
          <Avatar src={imageUrl ?? undefined} name={name} boxSize="184px" />
        </Box>
        {/* Text column */}
        <Flex
          gap="8px"
          flexDir="column"
          justify={{ base: "center", md: "start" }}
        >
          <Flex align="center" justify={{ base: "center", md: "start" }} mb={1}>
            <Heading as="h4" wordBreak="break-word">
              {name}
              {verified && (
                <Tooltip label="Verificado">
                  <Box ml="8px" display="inline-block">
                    <VerifiedDot size={22} />
                  </Box>
                </Tooltip>
              )}
            </Heading>
          </Flex>
          <Flex
            align="center"
            justify={{ base: "center", md: "start" }}
            gap="16px"
            wrap="wrap"
            mb="8px"
          >
            <Text
              fontWeight="semibold"
              color={useColorModeValue("gray.700", "gray.200")}
              fontSize="md"
            >
              {formatFollowers(displayFollowers)}
            </Text>
            {categories.length > 0 && (
              <CategoryTag
                label={categories[0]?.name}
                borderRadius="md"
                px={3}
                py={1}
              />
            )}
          </Flex>
          <ChannelFollowingButton
            isFollowing={displayIsFollowing}
            isFollowLoading={isFollowLoading}
            onClick={() =>
              isUserLoggedIn
                ? onToggleFollow?.(channel.id)
                : onLoginRequired?.(channel.id)
            }
            mx={{ base: "auto", md: "0" }}
            my={{ base: "8px", md: "0" }}
          />
          <ChannelSocialIcons social={social} />
        </Flex>
      </Flex>
      <Box>
        <Box
          color={useColorModeValue("gray.700", "gray.200")}
          maxW="4xl"
          sx={{
            "& li": {
              marginLeft: "18px",
            },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
        />
      </Box>
    </Flex>
  );
};
